import React, { useEffect, useState } from 'react';
import Card from 'react-bootstrap/Card';
import { formatToTimestamp, engineStat, engineStatClass, Distance, getAddressFromCoordinates, useClipboard } from '../../utilities/functions';
import { Row } from 'react-bootstrap';
import { useTranslate } from "../../hooks/LanguageProvider";
import { PropagateLoader } from 'react-spinners';

interface VehicleWidgetProps {
  id: number;
  matriculation: string;
  LAT: number;
  LON: number;
  SOG: number;
  COG: number;
  timestamp: string;
  enginestat: number;
  gpsdist: number;
  detail: string;
  PSN: string;
  nom_conducteur: string;
  nom_groupe: string;
  prenom_conducteur: string;
  onClose: (matriculation: any) => void;
  handleItineraryClick: (PSN: any) => void;
  handleDistanceSpeedClick: (PSN: any) => void;
  handleFuelTankClick: (PSN: any) => void;
  handleTemperatureDiagramClick: (PSN: any) => void;

  // NEW: flags d'état pour styler les boutons
  isRIActive?: boolean;
  isDistanceActive?: boolean;
  isFuelActive?: boolean;
  isTempActive?: boolean;
}

const VehicleWidget: React.FC<VehicleWidgetProps> = ({
  id, matriculation, timestamp, SOG, LAT, LON, enginestat, gpsdist,
  nom_conducteur, nom_groupe, prenom_conducteur, detail, onClose, PSN,
  handleItineraryClick, handleTemperatureDiagramClick, handleFuelTankClick, handleDistanceSpeedClick,

  // flags d'état
  isRIActive = false,
  isDistanceActive = false,
  isFuelActive = false,
  isTempActive = false,
}) => {
  const { translate } = useTranslate();
  const [address, setAddress] = useState<string>(translate("None"));
  const [loading, setLoading] = useState<boolean>(false);
  const { copyToClipboard, copiedId } = useClipboard(translate("Matriculation Copied"));

  const getAddress = async () => {
    if (LAT && LON) {
      setLoading(true);
      try {
        const fetchedAddress = await Promise.race([
          getAddressFromCoordinates(LAT, LON),
          new Promise<string>((_, reject) =>
            window.setTimeout(() => reject(new Error("Address request timeout")), 7000)
          ),
        ]);
        setAddress(fetchedAddress);
      } catch (error) {
        console.error("Error retrieving address", error);
        setAddress(translate("None"));
      } finally {
        setLoading(false);
      }
    } else {
      setAddress(translate("None"));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (detail === "flex" && address === translate("None")) {
      getAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, LAT, LON]);

  return (
    <>
      <Card
        id={`v-${id.toString()}`}
        className={`map-card ${engineStatClass(enginestat, SOG)}`}
        style={{ width: '90%', marginBottom: "10px" }}
        onClick={(event) => { getAddress() }}
      >
        <Card.Body style={{ padding: "0.25rem" }}>
          <Card.Title style={{ fontSize: "17px" }}>
            <img src={engineStat(enginestat, SOG, translate).iconState} alt="" />  {matriculation}
            <i
              className='las la-chevron-left return-to-list'
              onClick={(event) => { event.stopPropagation(); onClose(matriculation); }}
            />
          </Card.Title>

          <Card.Subtitle style={{ fontSize: "14px" }} className="mb-2 ">
            <i className="las la-clock map-icon-orange" title="Date & time"></i> {timestamp}
          </Card.Subtitle>

          <Card.Text
            id={`location-${id}`}
            style={{
              marginBottom: "4px",
              cursor: 'pointer',
              position: 'relative',
              color: copiedId === id.toString() ? '#28a745' : '#000'
            }}
            title={translate("Click to copy GPS coordinates")}
            onClick={() => {
              if (LAT && LON && id.toString()) {
                const coordinates = `${LAT.toFixed(6)}, ${LON.toFixed(6)}`;
                copyToClipboard(coordinates, id.toString());
              }
            }}
          >
            <>
              <i className="las la-map-marker map-icon-orange" title="Address"></i>{' '}
              <span style={{ color: copiedId === id.toString() ? '#28a745' : '#000' }}>
                {LAT.toFixed(6)}, {LON.toFixed(6)}
              </span>

              {copiedId === id.toString() && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    padding: '2px 5px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                >
                  {translate("Coordinates Copied")}
                </span>
              )}
            </>
          </Card.Text>

          <Row className='row'>
            <span className='col-6'>
              <p style={{ fontSize: "13px", margin: "0" }} className="text-muted">
                <i className="las la-tachometer-alt map-icon-orange" style={{ fontSize: "20px" }} title="Speed"></i> {translate("Speed")}
              </p>
              <span>{SOG} km/h</span>
            </span>

            <span className='col-6'>
              <p style={{ fontSize: "13px", margin: "0" }} className="text-muted">
                <i className="las lar la-road map-icon-orange" title="Distence"></i> {translate("Odometer")}
              </p>
              <span>{Distance(gpsdist)}</span>
            </span>
          </Row>

          <Row className='row' style={{ display: detail, padding: '10px 0', transition: "box-shadow 0.3s, transform 0.3s" }}>
            <span className='col-6'>
              <p style={{ fontSize: "13px", margin: "0" }} className="text-muted">
                <i className="las lar la-user-friends map-icon-orange"></i> {translate("Group")}
              </p>
              <span style={{ fontSize: "13px" }}>{nom_groupe ? nom_groupe : translate("None")}</span>
            </span>

            <span className='col-6'>
              <p style={{ fontSize: "13px", margin: "0" }} className="text-muted">
                <i className="las lar la-tag  map-icon-orange"></i> {translate("Driver")}
              </p>
              <span style={{ fontSize: "13px" }}>
                {nom_conducteur ? (nom_conducteur + " " + prenom_conducteur) : translate("None")}
              </span>
            </span>

            <span className='col-12' style={{ margin: "5px 0" }}>
              {loading ? (
                <p className="text-center" style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "13px", margin: "0" }} className="text-muted">
                    <PropagateLoader color={"#123abc"} loading={loading} size={8} />
                  </span>
                </p>
              ) : (
                <p style={{ fontSize: "13px", margin: "0" }}>
                  <i className="las la-home map-icon-orange" style={{ fontSize: "22px" }} title="Address"></i> {address}
                </p>
              )}
            </span>

            <h6>{translate("Report")} & {translate("Statistics")}</h6>

            {/* R.I. */}
            <div
              className='col-6'
              style={{ cursor: 'pointer', textAlign: "center" }}
              onClick={(event) => { event.stopPropagation(); handleItineraryClick(PSN); }}
              role="button"
              aria-pressed={isRIActive}
            >
              <p
                className={`report-tile ${isRIActive ? 'active' : ''}`}
                style={{ border: '1px solid rgb(221, 221, 221)', borderRadius: '5px', padding: '10px' }}
              >
                <i className="las la-Itinerary reconstitution" style={{ fontSize: '28px' }} />
                <img src="asset/images/icon-report/1.png" style={{ width: '32px', marginRight: '5px' }} alt="Icon" />
                <span style={{ margin: 0, fontSize: "14px", display: 'block' }}>{translate("Itinerary reconstitution")}</span>
              </p>
            </div>

            {/* Distance */}
            <div
              className='col-6'
              style={{ cursor: 'pointer', textAlign: "center" }}
              onClick={(event) => { event.stopPropagation(); handleDistanceSpeedClick(PSN); }}
              role="button"
              aria-pressed={isDistanceActive}
            >
              <p
                className={`report-tile ${isDistanceActive ? 'active' : ''}`}
                style={{ border: '1px solid rgb(221, 221, 221)', borderRadius: '5px', padding: '10px' }}
              >
                <i className="las la-Itinerary reconstitution" style={{ fontSize: '28px' }} />
                <img src="asset/images/icon-report/4.png" style={{ width: '32px', marginRight: '5px' }} alt="Icon" />
                <span style={{ margin: 0, fontSize: "14px", display: 'block' }}>{translate("Distance diagram")}</span>
              </p>
            </div>

            {/* Fuel */}
            <div
              className='col-6'
              style={{ cursor: 'pointer', textAlign: "center" }}
              onClick={(event) => { event.stopPropagation(); handleFuelTankClick(PSN); }}
              role="button"
              aria-pressed={isFuelActive}
            >
              <p
                className={`report-tile ${isFuelActive ? 'active' : ''}`}
                style={{ border: '1px solid rgb(221, 221, 221)', borderRadius: '5px', padding: '10px' }}
              >
                <i className="las la-Itinerary reconstitution" style={{ fontSize: '28px' }} />
                <img src="asset/images/icon-report/5.png" style={{ width: '32px', marginRight: '5px' }} alt="Icon" />
                <span style={{ margin: 0, fontSize: "14px", display: 'block' }}>{translate("Fuel Tank Diagram")}</span>
              </p>
            </div>

            {/* Temp */}
            <div
              className='col-6'
              style={{ cursor: 'pointer', textAlign: "center" }}
              onClick={(event) => { event.stopPropagation(); handleTemperatureDiagramClick(PSN); }}
              role="button"
              aria-pressed={isTempActive}
            >
              <p
                className={`report-tile ${isTempActive ? 'active' : ''}`}
                style={{ border: '1px solid rgb(221, 221, 221)', borderRadius: '5px', padding: '10px' }}
              >
                <i className="las la-Itinerary reconstitution" style={{ fontSize: '28px' }} />
                <img src="asset/images/icon-report/19.png" style={{ width: '32px', marginRight: '5px' }} alt="Icon" />
                <span style={{ margin: 0, fontSize: "14px", display: 'block' }}>{translate("Temperature Diagram")}</span>
              </p>
            </div>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default VehicleWidget;
